<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import KpButton from '@/components/KpButton.vue';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import {
    KorrekturWerteStromerzeugung,
    KorrekturWerteStromerzeugungCollection,
} from '@/models/KorrekturWerteStromerzeugung';
import { NutzungsartCollection, type Nutzungsart } from '@/models/Nutzungsart';
import CopyJahrDialog from '@/components/CopyJahrDialog.vue';
import { instance as api } from '@/helpers/api';
import type { DataTableRowExpandEvent } from 'primevue/datatable';
import KpNumberField from '@/components/KpNumberField.vue';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'jahr' },
    { direction: 'asc', property: 'kanton_kuerzel' },
    { direction: 'asc', property: 'referenz' },
]);
const expandedRowGroups: Ref<string[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-expandedRowGroups`, []);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new KorrekturWerteStromerzeugungCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

const nutzungsarten: Ref<Nutzungsart[]> = ref([]);
onMounted(async () => {
    if (collection.loading.value !== true) {
        collection.query();
    }
    const col = new NutzungsartCollection();
    col.setQueryParams({
        sort: [
            {
                property: 'reihenfolge',
                direction: 'asc',
            },
        ],
    });
    await col.query();
    col.each((m) => nutzungsarten.value.push(m));
});

function editRecord(r: KorrekturWerteStromerzeugung) {
    router.push({ name: 'bg_jw_korrektur_werte_stromerzeugung_edit', params: { id: r?.id } });
}

async function deleteRecord(r: KorrekturWerteStromerzeugung) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function onRowExpanded(event: DataTableRowExpandEvent) {
    expandedRowGroups.value = [event.data];
}

function newRecord() {
    router.push({ name: 'bg_jw_korrektur_werte_stromerzeugung_edit', params: { id: 'new' } });
}

const copyFromJahr = ref(0);
const showCopyDialog = ref(false);
function openCopyDialog(jahr: number) {
    copyFromJahr.value = jahr;
    showCopyDialog.value = true;
}

async function executeCopy(jahr: number, kanton: number) {
    try {
        await api().post('/KorrekturWerteStromerzeugung/copy', {
            from_jahr: copyFromJahr.value,
            to_jahr: jahr,
            kanton_id: kanton,
        });
        reloadCollection();
    } catch (e) {
        errToToast(e, toast);
    }
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            v-model:expandedRowGroups="expandedRowGroups"
            :title="$t('korrekturen_produktion')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            expandableRowGroups
            rowGroupMode="subheader"
            groupRowsBy="jahr"
            @rowgroup-expand="onRowExpanded"
            @editrecord="editRecord($event as KorrekturWerteStromerzeugung)"
            @deleterecord="deleteRecord($event as KorrekturWerteStromerzeugung)"
            @new="newRecord"
        >
            <template #groupheader="slotProps">
                <div class="inline-flex justify-content-between flex-wrap">
                    <div class="flex align-items-center justify-content-left ml-2 font-bold leading-normal">
                        {{ slotProps.data.jahr }}
                    </div>
                    <div class="flex align-items-center justify-content-right">
                        <KpButton
                            v-tooltip.bottom="$t('kopieren')"
                            icon="far fa-copy"
                            size="small"
                            severity="contrast"
                            variant="text"
                            rounded
                            @click="openCopyDialog(slotProps.data.jahr)"
                        />
                    </div>
                </div>
            </template>
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="jahr" :header="$t('jahr')" filter-field="jahr">
                <template #body="slotProps">
                    <div>{{ slotProps.data.jahr }}</div>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpNumberField
                        fluid
                        v-model="filterModel.value"
                        @change="filterCallback()"
                        :showClear="true"
                    ></KpNumberField>
                </template>
            </Column>
            <Column field="referenz" sortable :header="$t('referenz')"></Column>
            <Column field="kanton_kuerzel" sortable :header="$t('kanton')"></Column>
            <Column field="gemeinde" sortable :header="$t('gemeinde')"></Column>
            <Column field="technologie_stromerzeugung_id" :header="$t('technologie_stromerzeugung')">
                <template #body="slotProps">
                    <div>{{ slotProps.data.technologie_stromerzeugung }}</div>
                </template>
            </Column>
        </KpBaseCollectionGrid>
        <CopyJahrDialog v-if="showCopyDialog" v-model="showCopyDialog" @copy="executeCopy"></CopyJahrDialog>
    </div>
</template>
